import React, { useState, useEffect } from 'react';
import { Navbar } from '../shared/Navbar';
import { MessageSquare, ChevronDown, Glasses, FileText, SquareGantt, Layers, MonitorPlay, Target, PenLine, Brain } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function AskJDSNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('top');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  // Handle smooth scrolling to section IDs when hash links are clicked
  useEffect(() => {
    // Short delay to ensure the DOM has loaded
    const timeoutId = setTimeout(() => {
      if (location.hash) {
        const id = location.hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (id === 'top') {
          // Special case for #top if element not found
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location.hash]);

  // Scroll-based section detection
  useEffect(() => {
    if (location.pathname !== '/') return;

    const sections = ['top', 'caselaw', 'study', 'issue-spotter', 'essay-coach', 'intelligence', 'notes', 'outlines', 'chat', 'courses', 'non-services', 'pricing', 'about-section', 'why-jd-simplified'];
    
    const updateActiveSection = () => {
      const scrollPosition = window.scrollY + window.innerHeight * 0.3; // Offset for better UX
      
      // Get all section elements and their positions
      const sectionElements = sections.map(id => ({
        id,
        element: document.getElementById(id),
      })).filter(item => item.element);

      // Calculate section boundaries
      const sectionBoundaries = sectionElements.map(({ id, element }) => {
        const rect = element!.getBoundingClientRect();
        const absoluteTop = rect.top + window.scrollY;
        
        // For the last section, extend to the bottom of the page
        const isLastSection = id === sections[sections.length - 1];
        const nextSectionElement = sectionElements.find(s => 
          sections.indexOf(s.id) === sections.indexOf(id) + 1
        )?.element;
        
        let absoluteBottom;
        if (isLastSection) {
          absoluteBottom = document.body.scrollHeight;
        } else if (nextSectionElement) {
          const nextRect = nextSectionElement.getBoundingClientRect();
          absoluteBottom = nextRect.top + window.scrollY;
        } else {
          // Fallback: use element's height
          absoluteBottom = absoluteTop + element!.offsetHeight;
        }

        return {
          id,
          top: absoluteTop,
          bottom: absoluteBottom,
        };
      });

      // Find which section contains the current scroll position
      let activeId = 'top'; // default
      
      for (const section of sectionBoundaries) {
        if (scrollPosition >= section.top && scrollPosition < section.bottom) {
          activeId = section.id;
          break;
        }
      }

      // Only set active section if it has a corresponding nav item
      // Map why-jd-simplified to about-section since it's part of the About content
      if (activeId === 'why-jd-simplified') {
        setActiveSection('about-section');
      } else {
        setActiveSection(activeId);
      }
    };

    // Initial check
    updateActiveSection();

    // Add scroll listener with throttling
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateActiveSection, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [location.pathname]);
  
  // Handle direct click on hash links
  const handleHashLink = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    
    // Extract the hash from the href
    const hash = href.split('#')[1];
    
    if (location.pathname === '/') {
      // If already on homepage, scroll to section
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to homepage with hash
      navigate(`/#${hash}`);
    }
  };

  const isActive = (sectionId: string) => {
    if (location.pathname !== '/') {
      // Not on homepage, no active state
      return false;
    }
    
    // On homepage - check hash first (for direct navigation), then scroll detection
    if (location.hash) {
      const hashSection = location.hash.replace('#', '');
      return hashSection === sectionId;
    }
    
    // No hash, use scroll-based detection
    return activeSection === sectionId;
  };

  const featureItems = [
    { label: 'Case Briefs', href: '/#caselaw', icon: <Glasses size={16} /> },
    { label: 'Study', href: '/#study', icon: <Layers size={16} /> },
    { label: 'Issue Spotter', href: '/#issue-spotter', icon: <Target size={16} /> },
    { label: 'Essay Coach', href: '/#essay-coach', icon: <PenLine size={16} /> },
    { label: 'Intelligence', href: '/#intelligence', icon: <Brain size={16} /> },
    { label: 'Notes', href: '/#notes', icon: <FileText size={16} /> },
    { label: 'Outlines', href: '/#outlines', icon: <SquareGantt size={16} /> },
    { label: 'Chat', href: '/#chat', icon: <MessageSquare size={16} /> },
    { label: 'Courses', href: '/#courses', icon: <MonitorPlay size={16} /> },
  ];

  const navItems = [
    {
      label: 'Home',
      href: '/#top',
      isActive: isActive('top'),
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        if (location.pathname === '/') {
          // If already on homepage, scroll to top section
          const topElement = document.getElementById('top');
          if (topElement) {
            topElement.scrollIntoView({ behavior: 'smooth' });
          } else {
            // Fallback if element not found
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        } else {
          // Navigate to homepage with hash
          navigate('/#top');
        }
      }
    },
    {
      label: 'Features',
      href: '#',
      isActive: featureItems.some(item => {
        const sectionId = item.href.split('#')[1];
        return isActive(sectionId);
      }),
      customElement: (() => {
        const isFeaturesActive = featureItems.some(item => {
          const sectionId = item.href.split('#')[1];
          return isActive(sectionId);
        });
        return (
          <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button className={`relative px-3 py-2 transition-colors flex items-center gap-2 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:bg-[#F37022] after:transition-all ${
                isFeaturesActive
                  ? 'text-[#F37022] after:w-full'
                  : 'text-gray-700 dark:text-gray-300 hover:text-[#F37022] after:w-0 hover:after:w-full'
              }`}>
              Features
              <ChevronDown size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100">
            {featureItems.map((item) => (
              <DropdownMenuItem key={item.label} asChild className="hover:bg-[#F37022]/10 focus:bg-[#F37022]/10 hover:text-[#F37022] focus:text-[#F37022]">
                <a
                  href={item.href}
                  className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300"
                  onClick={(e) => {
                    e.preventDefault();
                    setDropdownOpen(false); // Close the dropdown
                    const hash = item.href.split('#')[1];
                    if (location.pathname === '/') {
                      const element = document.getElementById(hash);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    } else {
                      navigate(item.href);
                    }
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        );
      })(),
      // Mobile-specific rendering: provide featureItems as children
      mobileChildren: featureItems
    },
    {
      label: 'Non-Services',
      href: '/#non-services',
      isActive: isActive('non-services'),
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleHashLink(e, '/#non-services')
    },
    {
      label: 'Pricing',
      href: '/#pricing',
      isActive: isActive('pricing'),
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleHashLink(e, '/#pricing')
    },
    {
      label: 'About',
      href: '/#about-section',
      isActive: isActive('about-section'),
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => handleHashLink(e, '/#about-section')
    },
    {
      label: 'Blog',
      href: 'https://jdsimplified.com/blog/',
      isActive: false,
    },
  ];
  
  return (
    <Navbar
      siteName="askjds"
      navItems={navItems}
      logoSize="md"
      dashboardText="Access"
      dashboardHref="/chat"
    />
  );
} 